import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { API_URL } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import useRazorpayScript from '../../hooks/useRazorpayScript';
import GoogleMark from '../../components/GoogleMark';
import { normalizePhone, PHONE_ERROR, PHONE_ALLOWED } from '../../utils/phone';
import { mentorCompanies } from '../../components/home/CompanyStrip';
import '../Checkout/SessionCheckout.css';
import './ProgramCheckout.css';

// Reads an API response; a non-JSON reply (e.g. an HTML error page from an
// out-of-date server) becomes a readable error instead of a parse error
const readJson = async (res) => {
  try {
    return await res.json();
  } catch {
    return { success: false, message: 'We could not reach the billing service. Please try again in a moment.' };
  }
};

const rupees = (paise) => `₹${(paise / 100).toLocaleString('en-IN')}`;

// What the program includes, shown beside the payment
const PROGRAM_CONTENT = {
  'skill-development': {
    title: 'Real Engineering',
    tagline: 'All you need is 3 months with real engineers. Work like an engineer with 1–3 years of experience, then crack the interviews.',
    includes: [
      '3 months working with real engineers',
      'No prior experience required',
      'Work in an engineering team led by a senior engineer',
      'Tickets from a real backlog, planned in sprints',
      'Every pull request code-reviewed like at a product company',
      'Deploy to production and demo at sprint review',
      'Interview-ready project stories from real work',
      'Internship certificate on completion'
    ]
  }
};

// Billing for a paid program: details, coupon, Razorpay, then a success screen
const ProgramCheckout = () => {
  const { program: slug } = useParams();
  const content = PROGRAM_CONTENT[slug];
  const { user, loading: authLoading, signInWithGoogle, getAccessToken } = useAuth();
  const razorpay = useRazorpayScript();

  const [price, setPrice] = useState(null); // server quote
  const [loadError, setLoadError] = useState('');
  const [couponInput, setCouponInput] = useState('');
  const [couponError, setCouponError] = useState('');
  const [checkingCoupon, setCheckingCoupon] = useState(false);
  const [form, setForm] = useState({ name: '', phoneNumber: '' });
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const [paid, setPaid] = useState(null);
  const [phoneError, setPhoneError] = useState('');
  const [redirecting, setRedirecting] = useState(false);

  const fetchQuote = useCallback(async (coupon) => {
    const params = new URLSearchParams({ program: slug });
    if (coupon) params.set('coupon', coupon);
    // Signed-in users send their token so admin-only coupons can be checked
    const token = user ? await getAccessToken() : null;
    const res = await fetch(`${API_URL}/program-enrollments/quote?${params}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    const json = await readJson(res);
    if (!res.ok || !json.success) throw new Error(json.message || 'Could not load the price.');
    return json.data;
  }, [slug, user, getAccessToken]);

  const loadPrice = useCallback(() => {
    setLoadError('');
    fetchQuote()
      .then(setPrice)
      .catch((err) => setLoadError(err.message || 'Could not load the price.'));
  }, [fetchQuote]);

  useEffect(() => {
    if (content) loadPrice();
  }, [content, loadPrice]);

  // Prefill the name from the Google account
  useEffect(() => {
    const fullName = user?.user_metadata?.full_name || user?.user_metadata?.name || '';
    if (fullName) setForm((f) => (f.name ? f : { ...f, name: fullName }));
  }, [user]);

  const applyCoupon = async (e) => {
    e.preventDefault();
    // Apply with an empty box points them at the field instead
    if (!couponInput.trim()) {
      e.currentTarget.querySelector('input').focus();
      return;
    }
    setCouponError('');
    setCheckingCoupon(true);
    try {
      setPrice(await fetchQuote(couponInput.trim()));
      setCouponInput('');
    } catch (err) {
      setCouponError(err.message);
    } finally {
      setCheckingCoupon(false);
    }
  };

  const removeCoupon = async () => {
    setCouponError('');
    try {
      setPrice(await fetchQuote());
    } catch (err) {
      setCouponError(err.message);
    }
  };

  const startSignIn = async () => {
    setRedirecting(true);
    try {
      await signInWithGoogle(`/billing/${slug}`);
    } catch (err) {
      setRedirecting(false);
      setError(err.message || 'Could not start Google sign-in. Please try again.');
    }
  };

  const pay = async (e) => {
    e.preventDefault();
    if (!normalizePhone(form.phoneNumber)) {
      setPhoneError(PHONE_ERROR);
      return;
    }
    setError('');
    setPaying(true);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Your sign-in has expired. Please sign in again.');
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

      const res = await fetch(`${API_URL}/program-enrollments`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ program: slug, ...form, couponCode: price.coupon?.code || '' })
      });
      const json = await readJson(res);
      if (!res.ok || !json.success) throw new Error(json.message || 'Could not start the payment.');
      const order = json.data;

      const checkout = new window.Razorpay({
        key: order.keyId,
        amount: order.amount,
        currency: order.currency,
        order_id: order.orderId,
        name: 'Codeazy',
        description: order.programName,
        prefill: { name: order.name, email: order.emailAddress, contact: order.phoneNumber },
        theme: { color: '#a855f7' },
        handler: async (payment) => {
          try {
            const confirmRes = await fetch(`${API_URL}/program-enrollments/${order.id}/confirm`, {
              method: 'POST',
              headers: { ...headers, Authorization: `Bearer ${(await getAccessToken()) || token}` },
              body: JSON.stringify(payment)
            });
            const confirmJson = await readJson(confirmRes);
            if (!confirmRes.ok || !confirmJson.success) throw new Error(confirmJson.message || 'We could not verify the payment.');
            setPaid(confirmJson.data);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } catch (err) {
            setError(err.message);
          } finally {
            setPaying(false);
          }
        },
        modal: { ondismiss: () => setPaying(false) }
      });
      checkout.on('payment.failed', (res2) => {
        setError(res2.error?.description || 'Payment failed. Please try again.');
        setPaying(false);
      });
      checkout.open();
    } catch (err) {
      setError(err.message);
      setPaying(false);
    }
  };

  const shell = (children) => (
    <div className="sc-page">
      <header className="sc-topbar">
        <Link to="/" className="sc-logo">Codeazy</Link>
        <span className="sc-secure"><LockOutlinedIcon sx={{ fontSize: 15 }} /> Secure checkout</span>
      </header>
      <main className="sc-main">{children}</main>
    </div>
  );

  if (!content) {
    return shell(
      <div className="sc-card sc-card--message">
        <p>We couldn&apos;t find that program.</p>
        <Link to="/" className="sc-pay">Back to home</Link>
      </div>
    );
  }

  if (paid) {
    const firstName = (form.name || '').split(' ')[0];
    return shell(
      <div className="sc-card sc-card--message pc-success">
        <span className="pc-success-icon"><CheckIcon /></span>
        <h1>You&apos;re in{firstName ? `, ${firstName}` : ''}!</h1>
        <p>
          Payment of <strong>{rupees(paid.amountPaise)}</strong> received for the {price?.name}. We&apos;ll contact you at{' '}
          <strong>{user?.email}</strong> with your team and start date.
        </p>
        <p className="pc-payment-id">Payment ID: {paid.paymentId}</p>
        <Link to="/" className="sc-pay">Back to home</Link>
      </div>
    );
  }

  const listDiscount = price ? price.listPricePaise - price.pricePaise : 0;
  const listDiscountPercent = price ? Math.round((listDiscount / price.listPricePaise) * 100) : 0;
  const totalSaved = price ? price.listPricePaise - price.totalPaise : 0;

  return shell(
    <div className="sc-card">
      {/* Left: what they're joining */}
      <section className="sc-summary" aria-label="The program">
        <span className="pc-seats"><i /> Limited seats available</span>
        <h1>Join the <span className="sc-gradient">{content.title}</span> team</h1>
        <p className="sc-sub">{content.tagline}</p>

        <div className="sc-block">
          <h2>What&apos;s included</h2>
          <ul className="sc-gets">
            {content.includes.map((item) => <li key={item}><CheckIcon sx={{ fontSize: 14 }} />{item}</li>)}
          </ul>
        </div>

        <div className="sc-mentors">
          <span>Mentors from</span>
          <ul>
            {mentorCompanies.slice(0, 5).map((c) => (
              <li key={c.name} title={c.name}><img src={c.logo} alt={c.name} loading="lazy" /></li>
            ))}
          </ul>
        </div>
      </section>

      {/* Right: details, coupon and payment */}
      <section className="sc-payment" aria-label="Payment">
        {!price && loadError ? (
          <div className="pc-load-error">
            <p className="sc-error" role="alert">{loadError}</p>
            <button type="button" className="sc-pay" onClick={loadPrice}>Try again</button>
          </div>
        ) : !price ? (
          <div className="sc-loading" aria-busy="true"><span /><span /><span /></div>
        ) : (
          <>
            <h2>Order summary</h2>
            <div className="sc-line">
              <span>{price.name}</span>
              <span>{rupees(price.listPricePaise)}</span>
            </div>
            {listDiscount > 0 && (
              <div className="sc-line sc-line--discount">
                <span>Discount ({listDiscountPercent}% off)</span>
                <span>−{rupees(listDiscount)}</span>
              </div>
            )}
            {price.coupon && (
              <div className="sc-line sc-line--discount">
                <span className="pc-coupon-applied">
                  Coupon <code>{price.coupon.code}</code>
                  <button type="button" onClick={removeCoupon} aria-label="Remove coupon"><CloseIcon sx={{ fontSize: 14 }} /></button>
                </span>
                <span>−{rupees(price.coupon.amountOffPaise)}</span>
              </div>
            )}
            <div className="sc-total">
              <span>Total</span>
              <strong>{rupees(price.totalPaise)}</strong>
            </div>
            {totalSaved > 0 && <p className="sc-saving">You save {rupees(totalSaved)}</p>}

            {!price.coupon && (
              <form className="pc-coupon" onSubmit={applyCoupon}>
                <LocalOfferOutlinedIcon sx={{ fontSize: 18 }} />
                <input
                  value={couponInput}
                  onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCouponError(''); }}
                  placeholder="Have a coupon? Enter code"
                  aria-label="Coupon code"
                  autoComplete="off"
                />
                <button type="submit" disabled={checkingCoupon}>
                  {checkingCoupon ? 'Checking…' : 'Apply'}
                </button>
              </form>
            )}
            {couponError && <p className="pc-coupon-error" role="alert">{couponError}</p>}

            {authLoading ? (
              <div className="sc-loading" aria-busy="true"><span /><span /><span /></div>
            ) : !user ? (
              <div className="pc-signin">
                <p>Sign in to continue. Your enrolment and receipt go to your Google account.</p>
                <button type="button" className="pc-google" onClick={startSignIn} disabled={redirecting}>
                  <GoogleMark /> {redirecting ? 'Opening Google…' : 'Continue with Google'}
                </button>
                {error && <p className="sc-error" role="alert">{error}</p>}
              </div>
            ) : (
              <form className="pc-form" onSubmit={pay}>
                <label>
                  Full name
                  <input
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    required
                    autoComplete="name"
                  />
                </label>
                <label>
                  Email
                  <input type="email" value={user.email} readOnly title="From your Google account" />
                </label>
                <label>
                  Phone / WhatsApp
                  <input
                    type="tel"
                    value={form.phoneNumber}
                    onChange={(e) => {
                      if (!PHONE_ALLOWED.test(e.target.value)) return;
                      setPhoneError('');
                      setForm((f) => ({ ...f, phoneNumber: e.target.value }));
                    }}
                    onBlur={() => form.phoneNumber && !normalizePhone(form.phoneNumber) && setPhoneError(PHONE_ERROR)}
                    required
                    autoComplete="tel"
                    inputMode="tel"
                    maxLength={20}
                    placeholder="+91 98765 43210"
                    aria-invalid={Boolean(phoneError)}
                    aria-describedby={phoneError ? 'pc-phone-error' : undefined}
                  />
                  {phoneError && <span className="pc-field-error" id="pc-phone-error">{phoneError}</span>}
                </label>

                {error && <p className="sc-error" role="alert">{error}</p>}
                {razorpay.failed && <p className="sc-error" role="alert">Could not load the payment window. Please refresh.</p>}

                <button type="submit" className="sc-pay" disabled={paying || !razorpay.ready}>
                  <LockOutlinedIcon sx={{ fontSize: 18 }} />
                  {!razorpay.ready
                    ? 'Loading payment…'
                    : paying
                      ? 'Complete payment in the Razorpay window'
                      : `Pay ${rupees(price.totalPaise)} & join the team`}
                </button>
              </form>
            )}

            <ul className="sc-trust">
              <li><LockOutlinedIcon sx={{ fontSize: 14 }} /> Secured by Razorpay</li>
              <li><BoltOutlinedIcon sx={{ fontSize: 14 }} /> Instant confirmation</li>
            </ul>
            <ul className="sc-methods" aria-label="Payment methods">
              <li>UPI</li>
              <li>Cards</li>
              <li>Net banking</li>
            </ul>
          </>
        )}
      </section>
    </div>
  );
};

export default ProgramCheckout;
